import { Fn }			from "ts-base/fn";
import { Endo }			from "ts-base/endo";
import { NotNull }		from "ts-base/notNull";
import { Is }			from "ts-base/is";
import { Predicate }	from "ts-base/predicate";
import { Arrays }		from "ts-base/arrays";
import { Maybe }		from "ts-base/maybe";
import { Equal }		from "ts-base/equal";
import { Monoid }		from "ts-base/monoid";

export namespace Sets {
	//-----------------------------------------------------------------------------
	//## type helpers

	/** an identity function, but takes mutability away */
	export const readonly	= <T>(self:Set<T>):ReadonlySet<T>	=> self;

	//-----------------------------------------------------------------------------
	//## construction

	export const empty	= <T>():Set<T>	=>
		new Set<T>();

	export const single	= <T>(item:T):Set<T> =>
		new Set<T>([ item ]);

	export const of		= <T>(items:Iterable<T>):Set<T>	=>
		new Set<T>(items);

	export const ofN	= <T>(...items:ReadonlyArray<T>):Set<T>	=>
		of(items);

	export const fromNullable = <T>(item:T|null):Set<T> =>
		item !== null ? single(item) : empty();

	export const clone	= <T>(self:ReadonlySet<T>):Set<T>	=>
		new Set<T>(self);

	export const toArray	= <T>(self:ReadonlySet<T>):Array<T>	=>
		Array.from(self);

	export const throughArray	= <S>(self:ReadonlySet<S>) => <T>(func:Fn<ReadonlyArray<S>, ReadonlyArray<T>>):Set<T>	=>
		of(func(toArray(self)));

	//-----------------------------------------------------------------------------
	//## type guards

	export const isItemOf	= <T>(self:ReadonlySet<T>):Is<unknown, T>	=> (value:unknown):value is T	=>
		self.has(value as T);

	//-----------------------------------------------------------------------------
	//## extraction

	export const has	= <T>(self:ReadonlySet<T>)	=> (value:T):boolean	=>
		self.has(value);

	export const forall	= <T>(self:ReadonlySet<T>) => (pred:Predicate<T>):boolean	=> {
		for (const it of self) {
			if (!pred(it))	return false;
		}
		return true;
	};

	export const exists	= <T>(self:ReadonlySet<T>) => (pred:Predicate<T>):boolean	=> {
		for (const it of self) {
			if (pred(it))	return true;
		}
		return false;
	};

	export const some	= exists;
	export const every	= forall;

	//-----------------------------------------------------------------------------
	//## modification

	export const add	= <T>(self:ReadonlySet<T>) => (value:T):Set<T>		=> {
		const out	= clone(self);
		out.add(value);
		return out;
	};

	export const remove	= <T>(self:ReadonlySet<T>) => (value:T):Set<T>		=> {
		const out	= clone(self);
		out.delete(value);
		return out;
	};

	export const setMember	= <T>(self:ReadonlySet<T>) => (value:T) => (has:boolean):Set<T>	=> {
		const had	= self.has(value);
		const out	= clone(self);
		if		(has && !had)	out.add(value);
		else if	(had && !has)	out.delete(value);
		return out;
	};

	export const modifyMember	= <T>(self:ReadonlySet<T>) => (value:T) => (func:Endo<boolean>):Set<T>	=>
		setMember(self)(value)(func(has(self)(value)));

	export const toggle	= <T>(self:ReadonlySet<T>) => (value:T):Set<T>		=>
		modifyMember(self)(value)(it => !it);

	//-----------------------------------------------------------------------------
	//## math

	export const union	= <T>(a:ReadonlySet<T>, b:ReadonlySet<T>):Set<T>	=>
		new Set([ ...a, ...b ]);

	export const intersection	= <T>(a:ReadonlySet<T>, b:ReadonlySet<T>):Set<T>	=>
		throughArray(a)(array =>
			array.filter(it => b.has(it))
		);

	export const difference	= <T>(a:ReadonlySet<T>, b:ReadonlySet<T>):Set<T>	=>
		throughArray(a)(array =>
			array.filter(it => !b.has(it))
		);

	export const intersects	= <T>(a:ReadonlySet<T>, b:ReadonlySet<T>):boolean	=>
		Array.from(a).some(it => b.has(it));

	//-----------------------------------------------------------------------------
	//## specials

	export const pluck	=	<S>(self:ReadonlySet<S>) => <K extends keyof S>(key:K):Set<S[K]>	=>
		map(self)(it => it[key]);

	//-----------------------------------------------------------------------------
	//## filtering

	export const filter	= <T>(self:ReadonlySet<T>) => (pred:Predicate<T>):Set<T>	=>
		throughArray(self)(array => array.filter(pred));

	export const filterIs	= <S>(self:ReadonlySet<S>)	=> <T extends S>(is:Is<S, T>):Set<T>	=>
		throughArray(self)(array => array.filter(is));

	//-----------------------------------------------------------------------------
	//## collapse

	export const collapse = <T>(self:ReadonlySet<Maybe<T>>):Set<T>	=>
		flatMap(self)(Maybe.toSet);

	export const collapseMap = <S>(self:ReadonlySet<S>) => <T>(func:Fn<S, Maybe<T>>):Set<T>	=>
		flatMap(self)(Fn.andThen(func, Maybe.toSet));

	export const collapseNullable	= <T>(self:ReadonlySet<T|null>):Set<T>	=>
		filterIs(self)(NotNull.is);

	export const collapseMapNullable = <S>(self:ReadonlySet<S>) => <T>(func:Fn<S, T|null>):Set<T>	=>
		collapseNullable(map(self)(func));

	//-----------------------------------------------------------------------------
	//## functor

	export const map = <S>(self:ReadonlySet<S>) => <T>(func:Fn<S, T>):Set<T> =>
		throughArray(self)(it => it.map(func));

	//-----------------------------------------------------------------------------
	//## applicative functor

	export const pure	= single;

	export const ap = <S>(self:ReadonlySet<S>) => <T>(funcs:ReadonlySet<Fn<S, T>>):Set<T> =>
		flatMap(funcs)(map(self));

	//-----------------------------------------------------------------------------
	//## monad

	export const flatMap = <S>(self:ReadonlySet<S>) => <T>(func:Fn<S, ReadonlySet<T>>):Set<T> =>
		flatten(map(self)(func));

	export const flatten = <T>(self:ReadonlySet<ReadonlySet<T>>):Set<T> =>
		throughArray(self)(array => Arrays.flatMap(array)(toArray));

	//-----------------------------------------------------------------------------
	//## typeclasses

	// uses builtin equality of set elements
	export const equalOfBuiltin	= <T>():Equal<ReadonlySet<T>>	=> (a:ReadonlySet<T>, b:ReadonlySet<T>):boolean	=> {
		if (a === b)			return true;
		if (a.size !== b.size)	return false;
		for (const aa of a)	if (!b.has(aa))	return false;
		return true;
	};

	export const unionedMut	= <T>():Monoid<Set<T>>	=> ({
		empty:	empty(),
		concat:	(a, b)	=> union(a, b),
	});

	export const unionedRo	= <T>():Monoid<ReadonlySet<T>>	=> ({
		empty:	empty(),
		concat:	(a, b)	=> union(a, b),
	});
}
